// Taşeron portalı — Belge Analizi durum çubuğu (tray). `AppLayout`'ta `<main>`'in kardeşi
// olarak BİR KEZ monte edilir; rota değişse de ekranda kalan tek yüzey (plan S17-D11/D13,
// tasarım review §7-§10). `DocumentAnalysisProvider`'ın kuyruğunu + (varsa) aktif analizin
// yoklamasını (`useDocumentAnalysis`) okuyup §8'in durum tablosunu render eder.
//
// KATMAN — DÜZELTME: plan S17-D11 metninde `--z-drawer` (65) yazar; tasarım review bunu
// KUSUR olarak düzeltti — `--z-drawer` (45) `--z-overlay`/`--z-modal`ın ALTINDADIR (tokens.css
// sıra yorumu); tepsi `--z-dropdown` (40) olduğundan çekmecenin de altında kalır. Plan metnindeki
// değerle tepsi odak-tuzaklı bir Radix Dialog'un (§60) ÜSTÜNE binip Tab ile erişilemez
// hâle gelirdi (bugün `DocumentUploadModal` ile üretilebilir bir durum). Tray modal
// DEĞİLDİR — `--z-dropdown` (40) kullanılır; böylece bir modal'ın overlay'i (50) onu
// gölgeler ve Radix `aria-hidden` ile inert eder. Literal z-index YASAK — yalnız token.
//
// ERİŞİLEBİLİRLİK (tasarım §10): `aria-live="polite"` YALNIZCA faz sınırlarında değişen
// metin taşıyan düğümlere konur (Yükleniyor → Analiz kuyrukta → Analiz sürüyor →
// Tamamlandı/Başarısız) — 3 saniyede bir değişen yüzdeyi ASLA sarmaz (`sr-only <progress>`
// + görsel çubuk yeterlidir, `aria-valuenow` ekran okuyucu isteğe bağlı okur). `Spinner`
// zaten `<output aria-live="polite">` render eder (`Spinner.tsx`) — burada ETİKETSİZ
// kullanılır (ikon her pollde AYNI kalır, duyuru tetiklenmez). <kbd>Esc</kbd> yalnız odak
// tray İÇİNDEYKEN tray'i DARALTIR (asla kapatmaz — sürmekte olan tek erişim noktasını
// kaybettirmez) ve global KAYDOLMAZ (modal'ların Esc'ini ÇALMAZ). Tray, içinde
// `document.activeElement` varken kendini KALDIRMAZ (`forceVisible`).

import {
  type ApiError,
  Button,
  type DocumentAnalysisDto,
  Spinner,
  currentCulture,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CircleX,
  Info,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router-dom';
import { type UploadQueueItem, useDocumentAnalysisQueue } from '../lib/DocumentAnalysisProvider';
import { formatBytes } from '../lib/format';
import { useDocumentAnalysis } from '../lib/useDocumentAnalysis';

type TrayPhaseKind =
  | 'hidden'
  | 'uploading'
  | 'starting'
  | 'start-failed'
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed';

interface TrayPhase {
  kind: TrayPhaseKind;
  partial: boolean;
}

function derivePhase(input: {
  queue: UploadQueueItem[];
  analysisUuid: string | null;
  starting: boolean;
  waitingForSlot: boolean;
  hasStartError: boolean;
  analysis: DocumentAnalysisDto | undefined;
}): TrayPhase {
  const hasActiveUpload = input.queue.some(
    (q) => q.status === 'pending' || q.status === 'uploading',
  );
  if (hasActiveUpload) {
    return { kind: 'uploading', partial: false };
  }
  if (input.starting) {
    return { kind: 'starting', partial: false };
  }
  // S19-D5 — tek taşeron-başına slot iki üreticiyle paylaşılır; `409` bir HATA değil bir
  // KUYRUK sinyalidir (provider Kural 4). `waiting-for-slot` diye ayrı bir TrayPhaseKind
  // AÇILMAZ — burada doğrudan `queued`'a katlanır ki render tarafında hiçbir özel dal
  // gerekmesin ve kullanıcı ikisini de AYNI "Analiz kuyrukta" olarak okusun.
  if (input.waitingForSlot) {
    return { kind: 'queued', partial: false };
  }
  if (input.hasStartError && input.analysisUuid === null) {
    return { kind: 'start-failed', partial: false };
  }
  if (input.analysisUuid !== null) {
    switch (input.analysis?.status) {
      case 'Succeeded':
        return { kind: 'succeeded', partial: input.analysis.partial };
      case 'Failed':
        return { kind: 'failed', partial: false };
      case 'Running':
        return { kind: 'running', partial: false };
      default:
        // 'Queued' ya da ilk yanıt henüz gelmedi — 202 zaten Queued ile açılır (§20).
        return { kind: 'queued', partial: false };
    }
  }
  if (input.queue.length > 0) {
    // Tüm dosyalar başarısız oldu (hiçbiri onaylanmadı) — kuyruk yine de bir makbuz
    // olarak görünür kalır; başlatılacak bir şey yoktur.
    return { kind: 'uploading', partial: false };
  }
  return { kind: 'hidden', partial: false };
}

/** Dosya adını gövde + uzantı olarak ayırır — uç ellipsis uzantıyı yerdiği için AYRI kırpılır. */
function splitFileName(name: string): { stem: string; ext: string } {
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) {
    return { stem: name, ext: '' };
  }
  return { stem: name.slice(0, idx), ext: name.slice(idx) };
}

const iconButtonClass =
  'shrink-0 p-1 text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function DocumentAnalysisTray({
  onHeightChange,
}: {
  onHeightChange?: (px: number) => void;
}) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const {
    queue,
    analysisUuid,
    starting,
    waitingForSlot,
    startError,
    removeQueueItem,
    cancelAllUploads,
    retryStart,
  } = useDocumentAnalysisQueue();
  const polled = useDocumentAnalysis(analysisUuid);
  // C1b (plan S25, D1/D12) — eskiden `/document-analysis/:uuid`'i VE yalnız etkin analizi
  // tanırdı; gelen kutusu artık tam genişlikte kalıcı bir sayfa (`DocumentAnalysisInboxPage`,
  // hem `/document-analysis` hem `/document-analysis/:uuid`) ve tepsi onun sticky footer'ındaki
  // `Onayla`nın üstüne oturuyordu, `sm` altında da tüm çubuğu kaplıyordu — dolgu bunu çözmez
  // (D12), sayfa zaten kendi durumunu gösteriyor. `/*` splat rotanın uuid'li/uuid'siz HER
  // iki biçimini de eşler (`matchPath` doğrulandı) — analiz kuyruktaki analizle eşleşip
  // eşleşmediğine bakılmaksızın tepsi bu rotanın TAMAMINDA gizlenir.
  const inboxMatch = useMatch('/document-analysis/*');

  const phase = derivePhase({
    queue,
    analysisUuid,
    starting,
    waitingForSlot,
    hasStartError: startError !== null,
    analysis: polled.analysis?.analysis,
  });

  const containerRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState(true);
  const prevKindRef = useRef<TrayPhaseKind>('hidden');

  // Faz sınırında varsayılan açılış: yüklemeye GİRERKEN genişlet (tasarım review §6 değişiklik
  // a, S19-D4 — "Stay expanded through queued/running"). DARALTMA kolu bilerek YOK: panel artık
  // sahip kararıyla analiz sürerken de açık kalır; kullanıcı yine de elle daraltabilir ve
  // `Esc` tray İÇİNDEYKEN hâlâ daraltır (aşağıdaki `handleKeyDown`, dokunulmadı).
  useEffect(() => {
    const prev = prevKindRef.current;
    if (prev !== 'uploading' && phase.kind === 'uploading') {
      setExpanded(true);
    }
    prevKindRef.current = phase.kind;
  }, [phase.kind]);

  const wantsHidden = phase.kind === 'hidden' || inboxMatch !== null;

  // Tray, odağı İÇİNDE tutarken kendini kaldırmaz (tasarım §10) — gizlenmek istediği anda
  // odak hâlâ içindeyse görünür kalır; odak gerçekten ÇIKINCA `onBlur` ile kapanır.
  const [forceVisible, setForceVisible] = useState(false);
  useEffect(() => {
    if (!wantsHidden) {
      setForceVisible(false);
      return;
    }
    setForceVisible(Boolean(containerRef.current?.contains(document.activeElement)));
  }, [wantsHidden]);

  // Tepsinin yüksekliği `AppLayout`a bildirilir; `<main>` o kadar alt dolgu ayırır (liste
  // sonundaki ⋮ tepsinin altında kalıp tıklanamaz olmasın). Hook sırası için erken dönüşten ÖNCE.
  const visible = !(wantsHidden && !forceVisible);
  useEffect(() => {
    const el = containerRef.current;
    if (!visible || !el) {
      onHeightChange?.(0);
      return;
    }
    onHeightChange?.(el.offsetHeight);
    const observer = new ResizeObserver(() => onHeightChange?.(el.offsetHeight));
    observer.observe(el); // tepsi açık/daralmış/faz değişiminde büyür-küçülür
    return () => {
      observer.disconnect();
      onHeightChange?.(0);
    };
  }, [visible, onHeightChange]);

  if (!visible) {
    return null;
  }

  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation(); // modal'ların kendi Esc'ini ÇALMA — yalnız tray içindeyken.
      setExpanded(false);
    }
  }

  function handleBlur(e: FocusEvent<HTMLElement>) {
    const next = e.relatedTarget;
    if (wantsHidden && !(next instanceof Node && e.currentTarget.contains(next))) {
      setForceVisible(false);
    }
  }

  return (
    <section
      ref={containerRef}
      aria-label={t('analysis.tray.regionLabel')}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className="fixed inset-x-4 bottom-4 z-[var(--z-dropdown)] flex max-h-80 flex-col overflow-hidden border border-border-strong bg-surface shadow-lg sm:inset-x-auto sm:right-4 sm:w-[22rem]"
    >
      {phase.kind === 'uploading' ? (
        <UploadingBody
          queue={queue}
          culture={culture}
          expanded={expanded}
          onToggleExpand={() => setExpanded((v) => !v)}
          onCancelAll={
            queue.some((q) => q.status === 'pending' || q.status === 'uploading')
              ? cancelAllUploads
              : undefined
          }
          onCancelItem={removeQueueItem}
        />
      ) : phase.kind === 'starting' || phase.kind === 'queued' || phase.kind === 'running' ? (
        <ProgressHeader
          icon={<Spinner className="h-4 w-4" />}
          label={t(`analysis.tray.${phase.kind}`)}
          expanded={expanded}
          onToggleExpand={() => setExpanded((v) => !v)}
        >
          {expanded ? <ReceiptList queue={queue} culture={culture} /> : null}
        </ProgressHeader>
      ) : phase.kind === 'succeeded' ? (
        <TerminalRow
          icon={<Check className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />}
          label={t(phase.partial ? 'analysis.tray.succeededPartial' : 'analysis.tray.succeeded')}
        >
          {analysisUuid ? (
            // S19-D4 değişiklik b — dört CTA'nın tek `primary`'siydi; hiçbir yüzen nesne
            // turuncu OLMAZ (tasarım review §6).
            <Button asChild size="sm" variant="secondary">
              <Link to={`/document-analysis/${analysisUuid}`}>
                {t('analysis.tray.viewResults')}
              </Link>
            </Button>
          ) : null}
        </TerminalRow>
      ) : phase.kind === 'failed' ? (
        <TerminalRow
          icon={<CircleX className="h-4 w-4 shrink-0 text-danger" aria-hidden="true" />}
          label={t('analysis.tray.failed')}
        >
          {analysisUuid ? (
            <Button asChild size="sm" variant="secondary">
              <Link to={`/document-analysis/${analysisUuid}`}>{t('analysis.tray.viewDetail')}</Link>
            </Button>
          ) : null}
        </TerminalRow>
      ) : (
        <StartFailedBody error={startError} onRetry={retryStart} />
      )}
    </section>
  );
}

function ProgressHeader({
  icon,
  label,
  expanded,
  onToggleExpand,
  onCancelAll,
  children,
}: {
  icon: ReactNode;
  label: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onCancelAll?: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2.5">
        {icon}
        {/* Yalnız BURASI aria-live: metin faz sınırında değişir, yüzde İÇERMEZ. */}
        <span aria-live="polite" className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
          {label}
        </span>
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={t(expanded ? 'analysis.tray.collapse' : 'analysis.tray.expand')}
          className={iconButtonClass}
        >
          {expanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        {onCancelAll ? (
          <button
            type="button"
            onClick={onCancelAll}
            aria-label={t('analysis.tray.cancelAll')}
            className={iconButtonClass}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {children}
    </>
  );
}

function UploadingBody({
  queue,
  culture,
  expanded,
  onToggleExpand,
  onCancelAll,
  onCancelItem,
}: {
  queue: UploadQueueItem[];
  culture: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onCancelAll?: () => void;
  onCancelItem: (id: string) => void;
}) {
  const { t } = useTranslation();
  const done = queue.filter((q) => q.status === 'done').length;
  return (
    <ProgressHeader
      icon={<Upload className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />}
      label={t('analysis.tray.uploading', { done, total: queue.length })}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      onCancelAll={onCancelAll}
    >
      {expanded ? (
        <ul className="max-h-56 overflow-y-auto border-t border-border">
          {queue.map((item) => (
            <TrayFileRow
              key={item.id}
              item={item}
              culture={culture}
              showProgress
              onCancel={onCancelItem}
            />
          ))}
        </ul>
      ) : null}
    </ProgressHeader>
  );
}

/** Yükleme bittikten sonra (starting/queued/running) kuyruğun makbuz görünümü. */
function ReceiptList({ queue, culture }: { queue: UploadQueueItem[]; culture: string }) {
  if (queue.length === 0) {
    return null;
  }
  return (
    <ul className="max-h-56 overflow-y-auto border-t border-border">
      {queue.map((item) => (
        <TrayFileRow key={item.id} item={item} culture={culture} showProgress={false} />
      ))}
    </ul>
  );
}

function TrayFileRow({
  item,
  culture,
  showProgress,
  onCancel,
}: {
  item: UploadQueueItem;
  culture: string;
  showProgress: boolean;
  onCancel?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { stem, ext } = splitFileName(item.name);
  const canCancel = onCancel && (item.status === 'pending' || item.status === 'uploading');
  return (
    <li className="flex items-center gap-2 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline text-sm text-fg">
          <span className="min-w-0 truncate">{stem}</span>
          <span className="shrink-0">{ext}</span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted">
          <span className="shrink-0">{formatBytes(item.size, culture)}</span>
          {item.status === 'failed' ? (
            <span className="inline-flex items-center gap-1 text-danger">
              <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('analysis.tray.uploadFailed')}
            </span>
          ) : item.status === 'done' ? (
            <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
          ) : showProgress ? (
            <div className="flex flex-1 items-center gap-2">
              <progress
                className="sr-only"
                value={item.progress}
                max={100}
                aria-label={item.name}
              />
              <div className="h-1.5 flex-1 overflow-hidden bg-surface-muted" aria-hidden="true">
                <div
                  className="h-full bg-primary transition-[width] duration-150"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {canCancel ? (
        <button
          type="button"
          onClick={() => onCancel?.(item.id)}
          aria-label={t('analysis.tray.cancelUpload', { name: item.name })}
          className={iconButtonClass}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </li>
  );
}

function TerminalRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5">
      {icon}
      <span aria-live="polite" className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * Başlatma hatasının ÜÇ afordans biçiminden hangisi (tasarım review §8, "Nine start error
 * codes, three affordance shapes"; plan §2.1'in dokuz kod sırası). Kod tabanlı, sabit —
 * yeni/bilinmeyen bir hata kodu güvenli varsayılana (`nothing`, aksiyonsuz) düşer; bir
 * kalıcı hatayı sonsuz "Tekrar dene" döngüsüne sokmaz.
 *
 *  - `retry`   — `503 DOCUMENT_ANALYSIS_UNAVAILABLE` (geçici; aynı belgelerle yeniden POST
 *                anlamlı).
 *  - `go-here` — `409 DOCUMENT_ANALYSIS_IN_PROGRESS` **`messageData.analysisUuid` DOLU**
 *                (taşeron-başına engel) → o analize bağlantı.
 *  - `nothing` — `409` **`messageData` BOŞ** (firma kuyruk derinliği — engelleyen analiz
 *                BAŞKA bir taşerona ait olabilir, bağlantı ASLA verilmez — kiralık sızıntı),
 *                `429` kota, `403` (NOT_ENABLED / COMPANY_NOT_ACCESSIBLE), `404`/`400`
 *                (CONTRACTOR_NOT_FOUND / DOCUMENT_NOT_FOUND / VALIDATION_FAILED — bozuk
 *                paket ya da erişim). Yapılacak bir şey yok — yeniden denemek aynı sonucu
 *                verir ya da anlamsızdır.
 *
 * NOT — DOCCHECK_BUSY tasarım review'da "retry" grubunda anılır ama bu ÇAĞRININ (başlatma
 * POST'unun) hata kodu DEĞİLDİR: yalnız TERMİNAL `Failed` bir analizin `failureCode`'udur
 * (GET yanıtı, T8/sonuç sayfası kapsamı) — burada asla görünmez, exhaustive değildir.
 */
type StartErrorShape = 'retry' | 'go-here' | 'nothing';

function classifyStartError(error: ApiError): {
  shape: StartErrorShape;
  analysisUuid: string | null;
} {
  if (error.errorCode === 'DOCUMENT_ANALYSIS_UNAVAILABLE') {
    return { shape: 'retry', analysisUuid: null };
  }
  if (error.errorCode === 'DOCUMENT_ANALYSIS_IN_PROGRESS') {
    const uuid =
      typeof error.messageData?.analysisUuid === 'string' ? error.messageData.analysisUuid : null;
    return uuid
      ? { shape: 'go-here', analysisUuid: uuid }
      : { shape: 'nothing', analysisUuid: null };
  }
  return { shape: 'nothing', analysisUuid: null };
}

function StartFailedBody({
  error,
  onRetry,
}: {
  error: ApiError | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (!error) {
    // Kuyruktaki HER dosya başarısız yüklendiyse `startError` boştur (başlatılacak bir şey
    // yoktu, bir hata UYDURULMAZ) — kuyruk zaten kendi "Yüklenemedi" satırlarını gösterir.
    return null;
  }
  const { shape, analysisUuid } = classifyStartError(error);
  const Icon = shape === 'retry' ? TriangleAlert : shape === 'go-here' ? Info : CircleAlert;
  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-2 px-3 py-2.5">
        {/* `text-warning-foreground` grafittir — koyu temada grafit yüzeyin üstünde 1.00:1,
            yani glif GÖRÜNMEZ olur. Token'ın kendi kuralı "sarı ZEMİN + grafit içerik";
            tek başına duran bir glif için doğru olan `text-fg`. Tepsi, aynı sayfadaki
            şeritlerle (`StrandedAnalysesStrip`) yan yana durur ve onlarla AYNI çözümü alır. */}
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-fg" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          {/* İki ayrı gerçek — yükleme başarılı, başlatma değil (tasarım §8, "Copy for a
              failed start must separate two facts"); 503 gün-1 durumudur (S17-D10) ama bu
              başlık ÜÇ afordansın da ortak önsözüdür — hepsinde dosyalar zaten yüklendi. */}
          <p aria-live="polite" className="text-sm font-medium text-fg">
            {t('analysis.tray.startFailedTitle')}
          </p>
          <p className="mt-0.5 text-xs text-muted">{resolveApiErrorMessage(error, t)}</p>
        </div>
      </div>
      {shape === 'retry' ? (
        <div className="border-t border-border px-3 py-2">
          {/* İkincil (turuncu DEĞİL) — turuncu bu sayfada yalnız "Analizi başlat"a aittir;
              tray'in kendi "Sonuçları gör" (başarı) dışındaki her CTA'sı `secondary`dir,
              tıpkı terminal `Failed`'in "Ayrıntı"sı gibi (tutarlılık). */}
          <Button size="sm" variant="secondary" onClick={onRetry} className="w-full">
            {t('analysis.tray.retry')}
          </Button>
        </div>
      ) : shape === 'go-here' && analysisUuid ? (
        <div className="border-t border-border px-3 py-2">
          <Button asChild size="sm" variant="secondary" className="w-full">
            <Link to={`/document-analysis/${analysisUuid}`}>{t('analysis.tray.goToOngoing')}</Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
